import React, { useEffect, useState } from 'react';
import cn from 'classnames';
import { useLocation, useNavigate } from 'react-router-dom';
import { subscribeCondense } from 'Utils/scrollCondense';
import { isNewUIEnabled } from 'hooks/useNewUI';
import {
  AnimatedProjector,
  AnimatedLayoutDashboard,
  AnimatedFilm,
  AnimatedLayoutGrid,
  AnimatedCloudUpload,
  AnimatedGroup,
  AnimatedSettings,
} from 'Components/reactbits/AnimatedIcons';
import { Pathname } from 'Routes';
import styles from './index.module.css';

// Same primary nav + icon set as the bottom dock (V2Sidebar buildLinks /
// wheelIconFor), rendered as a floating rail using the lucide-animated icons.
// The viewer pages (/profile, /innerpage, /search, /watchlist) and /access keep
// the floating pill instead — gating lives in <Page>.
const NAV = [
  { key: 'projectors', label: 'Projectors', Icon: AnimatedProjector, to: Pathname.access, match: ['/access'] },
  { key: 'dashboard', label: 'Dashboard', Icon: AnimatedLayoutDashboard, to: Pathname.dashboard, match: ['/dashboard'] },
  { key: 'content', label: 'Content', Icon: AnimatedFilm, to: Pathname.yourVideos, match: ['/your-videos'] },
  { key: 'layout', label: 'Layout', Icon: AnimatedLayoutGrid, to: Pathname.contentLayout, match: ['/content-layout'] },
  {
    key: 'uploader', label: 'Uploader', Icon: AnimatedCloudUpload, to: Pathname.getStoragePath(Pathname.storage.videos), match: ['/storage'],
  },
  {
    key: 'viewers', label: 'Viewers', Icon: AnimatedGroup, to: Pathname.getViewersPath(Pathname.viewers.viewer), match: ['/viewers'],
  },
  {
    key: 'settings', label: 'Settings', Icon: AnimatedSettings, to: Pathname.settings.basePath, match: ['/settings', '/billings-and-payments', '/manage-payment-methods'],
  },
];

// App (management) sections where the persistent rail is shown. Everything else
// — /access, the viewer pages, players, public/auth flows — is left without it.
const SIDEBAR_SECTIONS = ['/dashboard', '/your-videos', '/content-layout', '/storage', '/settings', '/viewers'];
// The floating rail is a v2-chrome element — hidden entirely in the classic UI.
export const isFloatingSidebarRoute = (pathname) => isNewUIEnabled()
  && SIDEBAR_SECTIONS.some((p) => (pathname || '').includes(p));

const FloatingSidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(null);
  const [condensed, setCondensed] = useState(false);
  useEffect(() => subscribeCondense(setCondensed), []);
  const isActive = (item) => item.match.some((m) => location.pathname.includes(m));

  // Single persistent instance (mounted above <Routes>). It simply shows/hides
  // by route so it never remounts between pages — feels like one floating object.
  if (!isFloatingSidebarRoute(location.pathname)) return null;

  return (
    <nav
      className={cn(styles.rail, { [styles.railHidden]: condensed })}
      aria-label="Primary navigation"
      onMouseLeave={() => setHovered(null)}
    >
      {NAV.map((item) => {
        const { key, label, Icon, to } = item;
        const active = isActive(item);
        return (
          <button
            key={key}
            type="button"
            className={cn(styles.item, { [styles.active]: active })}
            onClick={() => navigate(to)}
            onMouseEnter={() => setHovered(key)}
            aria-label={label}
            aria-current={active ? 'page' : undefined}
          >
            <span className={styles.icon}><Icon size={22} animate={hovered === key} /></span>
            <span className={styles.label}>{label}</span>
          </button>
        );
      })}
    </nav>
  );
};

export default FloatingSidebar;
